import {Box, Button, GlobalStyles, Stack, Typography} from "@mui/material";
import {c, mono, maxW, pillButtonSx} from "../landingTokens";
import {VoiceBars} from "./VoiceBars";

const scrollTo = (id) => () =>
  document.getElementById(id)?.scrollIntoView({behavior: "smooth", block: "start"});

export const Hero = () => (
  <Box component="section" data-screen-label="Hero" sx={{position: "relative", px: 2.75, pt: {xs: 9, md: 12}, pb: {xs: 4, md: 7}, textAlign: "center", overflow: "hidden"}}>
    <GlobalStyles
      styles={{
        "@keyframes bfPulse": {"0%,100%": {transform: "scaleY(0.22)"}, "50%": {transform: "scaleY(1)"}},
        "@keyframes bfDrift": {
          "0%,100%": {transform: "translate3d(0,0,0) scale(1)"},
          "50%": {transform: "translate3d(3%,-4%,0) scale(1.08)"},
        },
      }}
    />

    {/* Purple bloom behind the headline */}
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: -320,
        left: "50%",
        transform: "translateX(-50%)",
        width: 1400,
        height: 900,
        background:
          "radial-gradient(ellipse at 50% 40%, rgba(139,124,255,0.42), rgba(220,235,250,0.65) 45%, rgba(248,249,252,0) 70%)",
        filter: "blur(10px)",
        pointerEvents: "none",
        animation: "bfDrift 14s ease-in-out infinite",
      }}
    />

    <Box sx={{position: "relative", maxWidth: 1000, mx: "auto"}}>
      <Typography sx={{fontFamily: mono, fontSize: "11.5px", letterSpacing: "0.16em", textTransform: "uppercase", color: c.purpleDeep, mb: 2.75}}>
        OpenBrain · AI Voice Agent
      </Typography>

      <Typography
        component="h1"
        sx={{fontSize: "clamp(46px, 8.2vw, 112px)", lineHeight: 0.93, letterSpacing: "-0.055em", fontWeight: 700, textWrap: "balance", textShadow: "0 12px 38px rgba(94,88,190,0.08)"}}
      >
        Cut contact center<br />workload by{" "}<br/>
        <Box component="span" sx={{display: "inline-flex", alignItems: "baseline", gap: {xs: 0.8, md: 1.5}, whiteSpace: "nowrap"}}>
          <Box component="span" sx={{background: `linear-gradient(100deg, ${c.purple}, ${c.purpleDeep})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"}}>50</Box>
          <Box component="span" sx={{fontSize: "0.58em", color: c.inkMuted, fontWeight: 500, letterSpacing: "-0.02em"}}>–</Box>
          <Box component="span" sx={{background: `linear-gradient(100deg, ${c.purpleDeep}, ${c.purpleInk})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"}}>70%</Box>
        </Box>
        <Box
          component="span"
          sx={{
            display: "none",
            background: `linear-gradient(100deg, ${c.purple}, ${c.purpleDeep} 72%, ${c.purpleInk})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          50–70%
        </Box>
        .
      </Typography>

      <Typography sx={{maxWidth: 650, mx: "auto", mt: 3.75, fontSize: {xs: 17, md: 21}, lineHeight: 1.5, color: c.inkSoft, textWrap: "pretty"}}>
        Our AI agent answers inbound calls, understands intent and resolves queries instantly — then hands off to your team with full context.
      </Typography>

      <Stack direction="row" gap={1.5} justifyContent="center" flexWrap="wrap" sx={{mt: 4.25}}>
        <Button
          onClick={scrollTo("contact")}
          variant="contained"
          sx={{...pillButtonSx, px: {xs: 2.5, md: 3.5}, py: {xs: 1.45, md: 1.75}, bgcolor: c.ink, color: c.white, boxShadow: "0 12px 26px -14px rgba(23,26,43,0.8)", "&:hover": {bgcolor: c.purpleDeep, boxShadow: "0 14px 30px -14px rgba(98,88,232,0.8)", transform: "translateY(-2px)"}}}
        >
          Book a free demo
        </Button>
        <Button
          onClick={scrollTo("how")}
          sx={{
            ...pillButtonSx,
            px: {xs: 2.5, md: 3.5},
            py: {xs: 1.45, md: 1.75},
            color: c.ink,
            bgcolor: "rgba(255,255,255,0.7)",
            border: "1px solid rgba(44,43,42,0.14)",
            "&:hover": {borderColor: "rgba(44,43,42,0.35)", bgcolor: c.white},
          }}
        >
          See how it works
        </Button>
      </Stack>

      <Typography sx={{fontFamily: mono, fontSize: 11, color: c.inkMuted, mt: 2.5, letterSpacing: "0.05em"}}>
        Runs on your Avaya · EN · MK · SR · HR · GDPR-compliant
      </Typography>
    </Box>

    {/* Console window, cropped by the section's bottom edge */}
    <Box sx={{position: "relative", maxWidth: maxW, mx: "auto", mt: {xs: 6, md: 7.75}}}>
      <Box
        sx={{
          borderRadius: "32px 32px 0 0",
          overflow: "hidden",
          border: `1px solid rgba(44,43,42,0.1)`,
          borderBottom: "none",
          bgcolor: c.white,
          boxShadow: "0 42px 90px -40px rgba(98,88,232,0.45)",
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.875} sx={{height: 38, px: 2, bgcolor: c.bgSoft, borderBottom: `1px solid rgba(44,43,42,0.07)`}}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{width: 9, height: 9, borderRadius: "50%", bgcolor: "#e3e3e8"}} />
          ))}
          <Typography sx={{fontFamily: mono, fontSize: "10.5px", color: c.inkMuted, ml: 1.5}}>
            openbrain · live call console
          </Typography>
        </Stack>

        <Box
          sx={{
            aspectRatio: "16 / 8.6",
            display: "grid",
            placeItems: "center",
            background: `linear-gradient(180deg, ${c.bgTint}, ${c.white})`,
            backgroundImage: "repeating-linear-gradient(135deg, rgba(132,145,247,0.09) 0 2px, transparent 2px 12px)",
          }}
        >
          <Box sx={{textAlign: "center", p: 3}}>
            <VoiceBars />
            <Typography sx={{fontFamily: mono, fontSize: 12, color: c.inkMuted, mt: 2.5, letterSpacing: "0.16em", textTransform: "uppercase"}}>
              Live call console · coming soon
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);
